import { useState, useEffect } from 'react'
import { Download, X, Share } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'

const DISMISSED_KEY = 'shelvi_pwa_dismissed'

type InstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> }

export function PwaInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<InstallPromptEvent | null>(null)
  const [showAndroid, setShowAndroid] = useState(false)
  const [showIos, setShowIos] = useState(false)

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY)) return

    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true

    if (isStandalone) return

    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent)

    if (isIos) {
      setShowIos(true)
      return
    }

    function onBeforeInstall(e: Event) {
      e.preventDefault()
      setDeferredPrompt(e as InstallPromptEvent)
      setShowAndroid(true)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall)
  }, [])

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, '1')
    setShowAndroid(false)
    setShowIos(false)
  }

  async function handleInstall() {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') localStorage.setItem(DISMISSED_KEY, '1')
    setShowAndroid(false)
    setDeferredPrompt(null)
  }

  if (showAndroid) {
    return (
      <div className="flex items-center gap-2 border-b border-primary/20 bg-primary/5 px-4 py-2 text-sm text-foreground dark:border-primary/30 dark:bg-primary/10">
        <Download className="size-3.5 shrink-0 text-primary" />
        <span className="flex-1">Install Shelvi on your device for faster access.</span>
        <Button
          variant="outline"
          size="xs"
          className="shrink-0 border-primary/30 text-primary hover:bg-primary/10"
          onClick={() => void handleInstall()}
        >
          Install
        </Button>
        <Button variant="ghost" size="icon" className="size-6 shrink-0 text-muted-foreground" onClick={dismiss}>
          <X className="size-3.5" />
        </Button>
      </div>
    )
  }

  if (showIos) {
    return (
      <div className="flex items-center gap-2 border-b border-primary/20 bg-primary/5 px-4 py-2 text-sm text-foreground dark:border-primary/30 dark:bg-primary/10">
        <Share className="size-3.5 shrink-0 text-primary" />
        <span className="flex-1">
          Install Shelvi — tap <strong>Share</strong> then <strong>Add to Home Screen</strong>.
        </span>
        <Button variant="ghost" size="icon" className="size-6 shrink-0 text-muted-foreground" onClick={dismiss}>
          <X className="size-3.5" />
        </Button>
      </div>
    )
  }

  return null
}
